/**
 * Driver Marketplace — Phase 1
 * ---------------------------------------------------------------------
 * Amazon-Flex-style block claim experience. This page replaces the old
 * /driver bottom-sheet "my trips" view as the primary driver home.
 *
 * Phase 1 (this file):
 *   1. Full-bleed Google Map with the 8 KC sector polygons drawn as
 *      light-navy semi-transparent overlays.
 *   2. Each sector renders a circular count marker in its centroid
 *      showing the *available block count* (NO miles, NO heatmap).
 *   3. Tapping a count morphs into a floating "Marketplace Pill" listing
 *      the available blocks in that sector. The pill sits ON the map —
 *      no modal, no navigation away.
 *   4. The map is freely pannable / zoomable. No auto-recenter. The
 *      "Locate me" FAB is the ONLY thing that recenters on the driver.
 *   5. Driver location renders as the standard blue dot (Google's own
 *      circle + accuracy ring; no custom car icon).
 *
 * Phase 2 (next iteration):
 *   - Block detail view + Claim flow
 *   - Morphing route pill (collapsed phone/text → expanded ride detail)
 *   - Auto-advance pickup → dropoff → next stop
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, MapPin, X, ChevronRight, MessageCircle, User, RefreshCcw, Navigation } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";
import LocationPuck from "@/shared/maps/LocationPuck";
import LocationPermissionPrompt from "@/shared/maps/LocationPermissionPrompt";
import useUserPuck from "@/shared/maps/useUserPuck";
import AssignedBlockMode from "@/apps/driver/pages/AssignedBlockMode";
import AssignedBlocksSurface from "@/apps/driver/components/AssignedBlocksSurface";
import DriverAccountDrawer from "@/apps/driver/components/DriverAccountDrawer";
import DriverEarningsMorph from "@/apps/driver/components/DriverEarningsMorph";
import DriverMarketplaceFeed from "@/apps/driver/components/DriverMarketplaceFeed";
import DriverSupportDrawer from "@/apps/driver/components/DriverSupportDrawer";
import { surfaceStyle, surfaceClass } from "@/shared/motion/surface";

// ---------- constants -------------------------------------------------
const KC_DEFAULT_CENTER = { lat: 39.085, lng: -94.575 };
const KC_DEFAULT_ZOOM = 13;             // Street-grade — driver sees their own neighbourhood,
                                        // not the whole metro. Rider parity.
const USER_BLUE        = "#0074d4";     // matches rider RouteMap USER_BLUE

// Supply-tier color palette for sector polygons + count markers.
// Empty (0) → muted gray. Low (1-3) → blue. Medium (4-9) → orange.
// High (10+) → emerald. Active selection always inherits orange.
const SUPPLY_PALETTE = {
  empty:  { fill: "#515E7B", stroke: "#515E7B", marker: "#FFFFFF", text: "#0A1128", border: "#515E7B" },
  low:    { fill: "#5c97f7", stroke: "#5c97f7", marker: "#FFFFFF", text: "#0A1128", border: "#5c97f7" },
  medium: { fill: "#ed6500", stroke: "#ed6500", marker: "#FFFFFF", text: "#0A1128", border: "#ed6500" },
  high:   { fill: "#10b981", stroke: "#10b981", marker: "#FFFFFF", text: "#0A1128", border: "#10b981" },
  active: { fill: "#ed6500", stroke: "#FFFFFF", marker: "#ed6500", text: "#FFFFFF", border: "#FFFFFF" },
};

function supplyTier(count) {
  if (!count || count <= 0) return "empty";
  if (count <= 3) return "low";
  if (count <= 9) return "medium";
  return "high";
}

// We load Google Maps the same way RouteMap does — via the API key in env.
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// ---------- page ------------------------------------------------------
export default function DriverMarketplace() {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSector, setActiveSector] = useState(null);   // sector currently open as pill

  // Phase-2 state — block detail + claim + assigned-block mode --------
  const [detailBlock, setDetailBlock] = useState(null);     // block currently open in detail screen
  const [claiming, setClaiming] = useState(false);
  // The driver's full schedule (claimed + in_progress). Loaded from
  // /api/driver/marketplace/assigned. We derive the Current Block
  // (in_progress OR earliest claimed) + Pending list (everything else)
  // from this single source of truth so the three pillars stay in sync.
  const [assignedBlocks, setAssignedBlocks] = useState([]);
  const [activeChecked, setActiveChecked] = useState(false);// have we hit /assigned at least once?
  const [currentBlockOpen, setCurrentBlockOpen] = useState(false); // is the Current Block screen open?
  const [assignedBlocksOpen, setAssignedBlocksOpen] = useState(false); // is the Schedule drawer open?
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [earningsOpen, setEarningsOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [todayEarnings, setTodayEarnings] = useState(0);
  const [meSummary, setMeSummary] = useState(null);

  // Lightweight identity fetch for the avatar initial in the bottom pill
  useEffect(() => {
    let cancelled = false;
    api.get("/driver/me")
      .then(({ data }) => { if (!cancelled) setMeSummary(data); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);
  // Today's earnings — polled every 30s so the live pill updates as
  // blocks/stops complete. Cheap call (single doc aggregation).
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/driver/earnings");
        if (!cancelled) setTodayEarnings(Number(data?.today?.gross || 0));
      } catch { /* non-fatal */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  const driverInitial = useMemo(() => {
    return ((meSummary?.first_name || meSummary?.name || "D").trim().charAt(0) || "D").toUpperCase();
  }, [meSummary]);

  // ---- Load the driver's schedule (claimed + in_progress) -----------
  // Single source of truth. Refreshed on mount, after every claim /
  // start / complete, and on a 30 s background poll so the surfaces
  // catch any state change made on another device.
  const loadAssigned = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/marketplace/assigned");
      setAssignedBlocks(Array.isArray(data?.blocks) ? data.blocks : []);
    } catch (_e) { /* non-fatal */ }
    finally { setActiveChecked(true); }
  }, []);

  useEffect(() => { loadAssigned(); }, [loadAssigned]);
  useEffect(() => {
    const id = setInterval(loadAssigned, 30000);
    return () => clearInterval(id);
  }, [loadAssigned]);

  // Derived: Current Block = in_progress block, OR (if none) the
  // earliest claimed block. Pending list = every claimed block NOT
  // currently displayed as Current Block. The user's auto-promotion
  // choice (b) — visually promote, do NOT auto-start.
  const currentBlock = useMemo(() => {
    if (assignedBlocks.length === 0) return null;
    const inProgress = assignedBlocks.find((b) => b.status === "in_progress");
    if (inProgress) return inProgress;
    // Earliest claimed by (date, window_start). /assigned already sorts.
    return assignedBlocks.find((b) => b.status === "claimed") || null;
  }, [assignedBlocks]);

  const pendingBlocks = useMemo(() => {
    return assignedBlocks.filter((b) => b.id !== currentBlock?.id && b.status === "claimed");
  }, [assignedBlocks, currentBlock]);

  // Patch a single block in place (used by AssignedBlockMode child
  // when /start, /stops/.../complete, etc. return the updated doc).
  const updateBlockInPlace = useCallback((updated) => {
    if (!updated || !updated.id) return;
    setAssignedBlocks((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  }, []);

  // ---- Load sectors on mount + poll (light: every 30s) ---------------
  const loadSectors = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/marketplace/sectors");
      setSectors(data?.sectors || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load sectors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSectors();
    const id = setInterval(loadSectors, 30000);
    return () => clearInterval(id);
  }, [loadSectors]);

  // ---- Open marketplace pill ----------------------------------------
  // Sets the active sector — the bottom marketplace drawer auto-opens
  // and filters to this sector via the `activeSector` prop.
  const openSector = useCallback((sector) => {
    setActiveSector(sector || null);
  }, []);

  const closeSector = useCallback(() => {
    setActiveSector(null);
  }, []);

  // ---- Phase 2 — open block detail screen from a pill row -----------
  const openBlock = useCallback(async (blockSummary) => {
    setDetailBlock({ ...blockSummary, _loading: true });
    try {
      const { data } = await api.get(`/driver/marketplace/blocks/${blockSummary.id}`);
      setDetailBlock(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load block");
      setDetailBlock(null);
    }
  }, []);

  const closeDetail = useCallback(() => setDetailBlock(null), []);

  // ---- Phase 2 — atomic first-come-first-served claim --------------
  const claimBlock = useCallback(async () => {
    if (!detailBlock || claiming) return;
    setClaiming(true);
    try {
      await api.post(`/driver/marketplace/blocks/${detailBlock.id}/claim`, {});
      toast.success("Block claimed");
      setDetailBlock(null);
      setActiveSector(null);
      // Refresh the driver's full schedule + sector counts immediately.
      loadAssigned();
      loadSectors();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object") ? detail.message : formatApiError(detail);
      toast.error(msg || "Could not claim block");
      // If we already had an active block we didn't know about, surface it.
      if (detail?.code === "ALREADY_ACTIVE") loadAssigned();
      if (detail?.code === "ALREADY_CLAIMED") {
        setDetailBlock(null);
        loadSectors();
      }
    } finally {
      setClaiming(false);
    }
  }, [detailBlock, claiming, loadSectors, loadAssigned]);

  // ---- Active-block surface — unified pill ↔ drawer ↔ navigating ---
  // The pill now lives INSIDE AssignedBlockMode as one motion element.
  // We just need to mount it whenever there is an active block; the
  // surface manages its own collapsed/expanded state via open/onOpenChange.
  // (The standalone "Current block" pill button below is retired.)

  return (
    <div className="absolute inset-0 bg-[#F9F7F4] text-[#0A1128]" data-testid="driver-marketplace">
      {/* SectorMap — full-bleed background. */}
      <SectorMap
        sectors={sectors}
        activeSectorId={activeSector?.id || null}
        onSectorTap={openSector}
      />

      {/* Block detail screen — full-overlay sheet over the map */}
      {detailBlock && (
        <BlockDetailScreen
          block={detailBlock}
          claiming={claiming}
          onBack={closeDetail}
          onClaim={claimBlock}
        />
      )}

      {/* Initial spinner — only visible while we have no sectors yet */}
      {loading && sectors.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
          <div
            className={`rounded-full bg-white/85 px-5 h-10 inline-flex items-center gap-2 text-[12px] text-[#0A1128] ${surfaceClass()}`}
            style={surfaceStyle("chip")}
          >
            <Loader2 size={14} className="animate-spin" />
            Loading marketplace…
          </div>
        </div>
      )}

      {/* ===========================  Chrome  =========================== */}
      {/* Top gradient overlay — same edge-to-edge depth treatment used
       *  behind the rider chrome. Fades when any drawer / detail opens. */}
      {!detailBlock && (
        <div
          className={`pointer-events-none fixed top-0 inset-x-0 transition-opacity duration-200 ${(drawerOpen || earningsOpen || supportOpen) ? "opacity-0" : "opacity-100"}`}
          style={{
            height: "180px",
            zIndex: 20,
            background: "linear-gradient(180deg, rgba(249,247,244,0.92) 0%, rgba(249,247,244,0.55) 40%, rgba(249,247,244,0) 100%)",
          }}
          data-testid="driver-map-top-gradient"
        />
      )}

      {!detailBlock && (() => {
        const anyDrawer = drawerOpen || earningsOpen || supportOpen;
        const chromeCls = `transition-opacity duration-200 ${anyDrawer ? "opacity-0 pointer-events-none" : "opacity-100"}`;
        return (
        <>
          {/* Top-left — Link wordmark pill (h-12) */}
          <div
            className={`fixed pointer-events-none ${chromeCls}`}
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)", left: 12, zIndex: 30 }}
            data-testid="driver-map-chrome-brand"
          >
            <div
              className={`pointer-events-auto h-12 px-4 rounded-full bg-[#F9F7F4]/85 flex items-center justify-center ${surfaceClass()}`}
              style={surfaceStyle("chip")}
              data-testid="driver-map-brand-pill"
            >
              <img src="/assets/link-full-logo.svg?v=13" alt="Link" className="h-6 w-auto" data-testid="driver-map-logo" />
            </div>
          </div>

          {/* Top-right — Support pill (h-12, square). */}
          <div
            className={`fixed pointer-events-none ${chromeCls}`}
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)", right: 12, zIndex: 33 }}
          >
            <button
              type="button"
              onClick={() => setSupportOpen(true)}
              aria-label="Open support"
              className={`pointer-events-auto h-12 w-12 rounded-full bg-[#0A1128]/85 text-[#F9F7F4] flex items-center justify-center link-pressable ${surfaceClass()}`}
              style={surfaceStyle("chip", { theme: "dark" })}
              data-testid="driver-map-support-pill"
            >
              <MessageCircle size={20} strokeWidth={1.9} />
            </button>
          </div>

          {/* Earnings morph pill — top-center. */}
          <DriverEarningsMorph
            todayEarnings={todayEarnings}
            open={earningsOpen}
            onOpenChange={setEarningsOpen}
            hidden={anyDrawer && !earningsOpen}
          />

          {/* Pillar #3 — Current Block surface. Mounts when the driver
              has any in_progress block, OR (auto-promotion) the
              earliest claimed block. Status drives the inner UI:
                · claimed     → Ready-to-Start CTA
                · in_progress → live timeline + Navigate flow */}
          {currentBlock && (
            <AssignedBlockMode
              block={currentBlock}
              onChange={updateBlockInPlace}
              open={currentBlockOpen}
              onOpenChange={setCurrentBlockOpen}
              hidden={(drawerOpen || earningsOpen || supportOpen || assignedBlocksOpen) && !currentBlockOpen}
              onCompleteBlock={() => { setCurrentBlockOpen(false); loadSectors(); loadAssigned(); }}
            />
          )}

          {/* Pillar #2 — Assigned Blocks (Schedule) surface. Only
              shown when the driver has additional claimed blocks
              beyond the one already promoted to Current Block. */}
          <AssignedBlocksSurface
            blocks={pendingBlocks}
            open={assignedBlocksOpen}
            onOpenChange={setAssignedBlocksOpen}
            onBrowseMarketplace={() => { /* future: deep-link into the marketplace drawer */ }}
            availableCount={sectors.reduce((acc, s) => acc + (s.available_block_count || 0), 0)}
            onChanged={() => { loadAssigned(); loadSectors(); }}
            hidden={(drawerOpen || earningsOpen || supportOpen || currentBlockOpen) && !assignedBlocksOpen}
          />

          {/* Pillar #1 — Marketplace. The bottom Top Opportunity / 14d
              drawer is suppressed once the driver has a Current
              Block — at that point they're executing, not shopping
              (per spec "Marketplace should NEVER display navigation
              or active trip information"). */}
          <>
            {!currentBlock && (
              <DriverMarketplaceFeed
                sectors={sectors}
                bottomOffset={80}
                activeSector={activeSector}
                onCloseSector={closeSector}
                onClaimed={() => { loadAssigned(); loadSectors(); }}
              />
            )}
            {!activeSector && (
              <div
                className={`fixed left-1/2 -translate-x-1/2 pointer-events-none ${chromeCls}`}
                style={{ bottom: "max(env(safe-area-inset-bottom), 16px)", zIndex: 34 }}
              >
                <div
                  className={`pointer-events-auto rounded-full bg-white/85 p-1.5 inline-flex items-center gap-1 ${surfaceClass()}`}
                  style={surfaceStyle("chip")}
                  data-testid="driver-map-action-pill"
                >
                  <button
                    type="button"
                    className="h-10 px-5 rounded-full bg-[#0A1128] text-white text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-1.5 link-pressable"
                    data-testid="driver-map-tab-map"
                  >
                    <MapPin size={13} strokeWidth={2}/> Map
                  </button>
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    aria-label="Account"
                    className="h-10 px-5 rounded-full text-[#515E7B] hover:text-[#0A1128] text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-1.5 transition-colors link-pressable"
                    data-testid="driver-map-tab-account"
                  >
                    <User size={13} strokeWidth={2}/> Account
                  </button>
                </div>
              </div>
            )}
          </>
        </>
        );
      })()}

      <DriverAccountDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <DriverSupportDrawer open={supportOpen} onClose={() => setSupportOpen(false)} />
      <LocationPermissionPrompt />

      {/* Global animation keyframes for the driver map surface */}
      <style>{`
        @keyframes opp-in {
          0%   { opacity: 0; transform: translateY(14px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0)    scale(1); }
        }
        .animate-opp-in { animation: opp-in 380ms cubic-bezier(0.22, 1, 0.36, 1); }
      `}</style>
    </div>
  );
}


/* ===========================  Featured opportunity card  ===========================
 * Surfaces the single most-attractive available block right above the
 * MAP|ACCOUNT pill so the driver never has to hunt for work. Picks the
 * highest-pay block in the sector with the most availability (best
 * combination of "easy to claim" + "pays well"). Fetches lazily once
 * any sector reports availability; refreshes whenever sectors change. */
function FeaturedOpportunity({ sectors, onClaim }) {
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(false);

  const topSector = useMemo(() => {
    return (sectors || [])
      .filter((s) => (s.available_block_count || 0) > 0)
      .sort((a, b) => (b.available_block_count || 0) - (a.available_block_count || 0))[0] || null;
  }, [sectors]);

  // Keep the picked sector_id in a ref so we don't re-fetch when sectors
  // refresh but the leader hasn't changed.
  const lastSectorId = useRef(null);
  useEffect(() => {
    if (!topSector) { setFeatured(null); lastSectorId.current = null; return; }
    if (lastSectorId.current === topSector.id && featured) return;
    lastSectorId.current = topSector.id;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/driver/marketplace/sectors/${topSector.id}/blocks`);
        if (cancelled) return;
        const blocks = (data?.blocks || []).slice().sort((a, b) => (b.pay_dollars || 0) - (a.pay_dollars || 0));
        setFeatured(blocks[0] || null);
      } catch (_e) { /* non-fatal */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [topSector, featured]);

  if (!topSector || (!featured && !loading)) return null;
  return (
    <button
      type="button"
      onClick={() => featured && onClaim?.(featured)}
      className="pointer-events-auto w-[min(92vw,360px)] rounded-3xl bg-white border border-black/[0.06] shadow-[0_20px_50px_rgba(10,17,40,0.20)] p-5 text-left transition-transform animate-opp-in hover:-translate-y-0.5"
      data-testid="driver-map-opportunity"
    >
      <div className="text-[9.5px] tracking-[0.32em] uppercase font-semibold text-[#ed6500]" data-testid="opportunity-eyebrow">
        Top opportunity
      </div>
      {featured ? (
        <>
          <div className="font-serif text-[40px] leading-none text-[#0A1128] mt-1.5" data-testid="opportunity-pay">
            ${featured.pay_dollars.toFixed(0)}
          </div>
          <div className="text-[13.5px] text-[#0A1128] font-medium mt-2" data-testid="opportunity-sector">
            {topSector.name}
          </div>
          <div className="text-[12px] text-[#515E7B] mt-1" data-testid="opportunity-meta">
            {featured.stops_count} Stops · {featured.estimated_minutes ? `${Math.round(featured.estimated_minutes / 60 * 10) / 10} Hours` : "Flexible window"}
          </div>
          {featured.window_start && featured.window_end && (
            <div className="text-[11px] text-[#515E7B] mt-0.5" data-testid="opportunity-window">
              {formatTime12(featured.window_start)} – {formatTime12(featured.window_end)}
            </div>
          )}
          <span
            className="mt-4 w-full h-12 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-1.5"
            data-testid="opportunity-claim-cta"
          >
            Claim Block
            <ChevronRight size={14} strokeWidth={2.2} />
          </span>
        </>
      ) : (
        <div className="text-[12px] text-[#515E7B] mt-2"><Loader2 size={12} className="inline animate-spin mr-1" /> Loading top opportunity…</div>
      )}
    </button>
  );
}

/* ===========================  Empty marketplace sheet  ===========================
 * Bottom sheet shown when no sectors have any availability. Mirrors the
 * Rider App's empty-state pattern AND keeps the map informative by
 * listing the closest sectors (so the driver knows where work normally
 * lives, even on a quiet day). */
function EmptyMarketplaceSheet({ sectors = [], onRefresh, onViewAll }) {
  // Pick the first 3 sectors regardless of count (the API already sorts
  // them by city order). When all are at 0 we still want the driver to
  // see "Northland 0 · Downtown KC 0 · Liberty 0" so the map feels
  // intentional, not broken.
  const nearest = (sectors || []).slice(0, 3);
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 pointer-events-auto w-[min(94vw,400px)]"
      style={{ bottom: "calc(max(env(safe-area-inset-bottom), 16px) + 84px)", zIndex: 35 }}
      data-testid="driver-map-empty-sheet"
    >
      <div className="rounded-3xl bg-white border border-black/[0.06] shadow-[0_20px_50px_rgba(10,17,40,0.20)] p-6 text-center animate-opp-in">
        <div className="mx-auto w-12 h-12 rounded-full inline-flex items-center justify-center bg-[#ed6500]/10 text-[#ed6500] mb-3">
          <MapPin size={18} strokeWidth={1.8} />
        </div>
        <div className="font-serif text-[22px] leading-tight text-[#0A1128]">No blocks available</div>
        <p className="text-[12.5px] text-[#515E7B] mt-1.5 leading-relaxed">
          We&rsquo;re currently waiting for rider demand in your area. New blocks will appear here automatically.
        </p>
        {nearest.length > 0 && (
          <div className="mt-4 rounded-2xl bg-[#F9F7F4] border border-black/[0.05] divide-y divide-black/[0.05] text-left" data-testid="driver-map-empty-nearest">
            {nearest.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-3 py-2.5" data-testid={`driver-map-empty-sector-${s.id}`}>
                <span className="text-[12.5px] text-[#0A1128]">{s.name}</span>
                <span className="text-[10.5px] tracking-[0.2em] uppercase font-semibold text-[#515E7B]">
                  {s.available_block_count || 0} blocks
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onRefresh}
            className="h-11 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-1.5"
            data-testid="driver-map-empty-refresh"
          >
            <RefreshCcw size={13} strokeWidth={2.2}/> Refresh
          </button>
          <button
            type="button"
            onClick={onViewAll}
            className="h-11 rounded-2xl border border-[#0A1128]/15 hover:border-[#ed6500] text-[#0A1128] text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center"
            data-testid="driver-map-empty-viewall"
          >
            View all sectors
          </button>
        </div>
      </div>
    </div>
  );
}

/* ====================================================================
   Sector map — Google Maps + polygon overlays + count markers
   ==================================================================== */
function SectorMap({ sectors, activeSectorId, onSectorTap }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const polygonsRef = useRef({});       // sectorId → google.maps.Polygon
  const countMarkersRef = useRef({});   // sectorId → google.maps.Marker (HTML AdvancedMarker is overkill)
  const [mapReady, setMapReady] = useState(false);

  // ---- Bootstrap Google Maps once -----------------------------------
  useEffect(() => {
    if (!MAPS_KEY) return;
    if (window.google?.maps) { setMapReady(true); return; }
    // Reuse RouteMap's loader pattern: inject a single tagged script.
    const SCRIPT_ID = "google-maps-js";
    if (document.getElementById(SCRIPT_ID)) {
      const wait = setInterval(() => {
        if (window.google?.maps) { clearInterval(wait); setMapReady(true); }
      }, 100);
      return () => clearInterval(wait);
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&v=weekly&libraries=geometry,marker`;
    s.async = true;
    s.defer = true;
    s.onload = () => setMapReady(true);
    document.head.appendChild(s);
  }, []);

  // ---- Driver geolocation + heading puck (Uber/Lyft-style) ---------
  // The puck owns: high-accuracy GPS, Kalman smoothing, device-heading,
  // 60fps interpolation, and the directional cone fade. We keep a
  // `puckMode` state to flip between north-up follow and active-nav
  // (camera rotates with heading + tilt). The locate-me button is the
  // single permission/follow toggle.
  const puck = useUserPuck({ active: mapReady });
  const [puckMode, setPuckMode] = useState("static");
  const recenteredRef = useRef(false);
  useEffect(() => {
    if (!mapRef.current || !puck.hasFix || recenteredRef.current) return;
    recenteredRef.current = true;
    mapRef.current.panTo({ lat: puck.lat, lng: puck.lng });
    mapRef.current.setZoom(13);
  }, [puck.hasFix, puck.lat, puck.lng]);

  // ---- Create map once Google Maps is loaded ------------------------
  useEffect(() => {
    if (!mapReady || !containerRef.current || mapRef.current) return;
    mapRef.current = new window.google.maps.Map(containerRef.current, {
      center: KC_DEFAULT_CENTER,
      zoom: KC_DEFAULT_ZOOM,
      // mapId is required by AdvancedMarkerElement (and unlocks heading + tilt
      // for navigation mode). DEMO_MAP_ID is the public Google Maps fixture
      // and is fine for development; production should pass a real Map ID.
      mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID",
      disableDefaultUI: true,
      gestureHandling: "greedy",            // free pan/zoom, no two-finger requirement
      keyboardShortcuts: false,
      mapTypeId: "roadmap",
    });
    // Tapping empty map area dismisses the active sector — returns
    // the marketplace pill to its compact "best block" view.
    mapRef.current.addListener("click", () => {
      onSectorTap && onSectorTap(null);
    });
  }, [mapReady, onSectorTap]);

  // ---- (recenter-on-fix handled above with `puck.hasFix`) -----------

  // ---- Legacy pulsing blue dot REMOVED — replaced by <LocationPuck/>.

  // ---- Render / update sector polygons ------------------------------
  // V2 — REMOVED. Sector rectangles read as fleet-dispatch geometry and
  // were visually disconnected from the Rider App. Sectors now live in
  // backend logic only; the driver sees a small count marker + name pill
  // per sector (DoorDash style).
  useEffect(() => {
    const existing = polygonsRef.current;
    for (const id of Object.keys(existing)) {
      existing[id].setMap(null);
      delete existing[id];
    }
  }, [sectors, mapReady]);

  // ---- Render / update count markers (centered per sector) ----------
  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    const map = mapRef.current;
    const existing = countMarkersRef.current;
    const seen = new Set();
    for (const s of sectors) {
      const count = s.available_block_count || 0;
      // V3 — hide empty sectors entirely. The driver only sees where
      // actual opportunities exist.
      if (count <= 0) continue;
      seen.add(s.id);
      const [lat, lng] = s.center || [];
      if (typeof lat !== "number" || typeof lng !== "number") continue;
      const active = s.id === activeSectorId;
      const tier = supplyTier(count);
      const html = renderCountIcon({ count, active, tier, label: s.name });
      if (existing[s.id]) {
        existing[s.id].setIcon(html);
        existing[s.id].setPosition({ lat, lng });
      } else {
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map,
          icon: html,
          cursor: "pointer",
          // Render ABOVE the user-location puck (zIndex 1). Sectors are
          // the primary tap targets — the puck must never sit on top.
          zIndex: 100,
        });
        marker.addListener("click", () => onSectorTap(s));
        existing[s.id] = marker;
      }
    }
    for (const id of Object.keys(existing)) {
      if (!seen.has(id)) { existing[id].setMap(null); delete existing[id]; }
    }
  }, [sectors, activeSectorId, mapReady, onSectorTap]);

  // ---- Driver location dot REMOVED — the new <LocationPuck/> mounted
  //      below handles the user's GPS, heading, smoothing, and the
  //      Uber-style cone/dot rendering. Removing the old blue-dot
  //      effect prevents a stale `blueDotRef` runtime error and keeps
  //      only one puck on the map at a time.

  const recenterOnDriver = useCallback(async () => {
    // Bundled permission flow: location + DeviceOrientation (iOS Safari
    // needs this inside a user gesture). On grant we flip to "follow"
    // mode so the camera tracks the user; tapping again toggles into
    // navigation mode (camera rotates with heading + tilt) and a 3rd
    // tap returns to north-up follow. See LocationPuck for the modes.
    if (puckMode === "static") {
      const perm = await puck.request();
      if (perm === "denied") {
        toast.error("Location permission denied");
        return;
      }
      setPuckMode("follow");
    } else if (puckMode === "follow") {
      setPuckMode("navigation");
    } else {
      setPuckMode("follow");
    }
    if (puck.lat != null && mapRef.current) {
      mapRef.current.panTo({ lat: puck.lat, lng: puck.lng });
      if (mapRef.current.getZoom() < 17) mapRef.current.setZoom(17);
    }
  }, [puck, puckMode]);

  return (
    <>
      <div ref={containerRef} className="absolute inset-0" data-testid="driver-marketplace-map" />
      {/* The live user puck — Uber/Lyft-style smoothed dot + heading cone.
       *  Mounted as a child of SectorMap because it needs both the map
       *  ref AND the geolocation hook state. Headless: it side-effects
       *  the map. */}
      <LocationPuck
        map={mapRef.current}
        lat={puck.lat}
        lng={puck.lng}
        heading={puck.heading}
        headingConfident={puck.headingConfident}
        mode={puckMode}
        followZoom={17}
        onUserGesture={() => setPuckMode("static")}
      />
      {!MAPS_KEY && (
        <div className="absolute inset-0 flex items-center justify-center z-10 text-center text-[13px] text-white/70 px-6">
          Google Maps API key missing — set <code className="font-mono">REACT_APP_GOOGLE_MAPS_API_KEY</code> to render the marketplace map.
        </div>
      )}
      {/* Locate me FAB — bundled permission + follow/navigation toggle.
       *  Tap 1: request location + compass permission → enter follow mode.
       *  Tap 2: enter navigation mode (camera rotates with heading + tilt).
       *  Tap 3: back to follow mode (north-up).
       *  Icon swaps to a navigation glyph when nav mode is active so the
       *  driver always knows the current camera state at a glance. */}
      <button
        type="button"
        onClick={recenterOnDriver}
        className={`absolute right-4 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-colors z-20 ${puckMode === "navigation" ? "bg-[#1664E0] text-white" : puckMode === "follow" ? "bg-[#0A1128] text-white" : "bg-white text-[#0A1128] hover:bg-[#F9F7F4]"}`}
        style={{ bottom: "max(env(safe-area-inset-bottom), 16px)" }}
        aria-label={puckMode === "static" ? "Locate me" : puckMode === "follow" ? "Enable navigation" : "Exit navigation"}
        data-testid="driver-marketplace-locate"
      >
        {puckMode === "navigation"
          ? <Navigation size={18} strokeWidth={2} />
          : <MapPin size={18} strokeWidth={2} />}
      </button>
    </>
  );
}

/* ====================================================================
   Marketplace pill — appears when a sector count is tapped
   ==================================================================== */
function MarketplacePill({ sector, blocks, loading, error, onClose, onBlockTap }) {
  const list = useMemo(() => blocks || [], [blocks]);
  // Sector stats
  const avgPay = list.length ? list.reduce((s, b) => s + (b.pay_dollars || 0), 0) / list.length : 0;
  const avgMins = list.length ? Math.round(list.reduce((s, b) => s + (b.estimated_minutes || 0), 0) / list.length) : 0;
  const avgDur = avgMins ? `${Math.floor(avgMins / 60)}h ${avgMins % 60}m` : "—";
  return (
    <div data-testid="marketplace-pill">
      <div className="mx-auto max-w-md rounded-3xl bg-white border border-black/[0.06] shadow-[0_24px_60px_rgba(10,17,40,0.18)] overflow-hidden">
        {/* Drawer handle */}
        <div className="flex items-center justify-center pt-2">
          <span className="w-9 h-1 rounded-full bg-[#0A1128]/15" />
        </div>
        {/* Drawer header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-3 pb-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#ed6500]">Sector</div>
            <div className="font-serif text-[22px] leading-tight truncate text-[#0A1128]" data-testid="marketplace-pill-sector-name">
              {sector?.name}
            </div>
            <div className="text-[12px] text-[#515E7B] mt-0.5" data-testid="marketplace-pill-count">
              {list.length} block{list.length === 1 ? "" : "s"} available
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-full bg-[#F9F7F4] hover:bg-black/[0.06] inline-flex items-center justify-center transition-colors text-[#0A1128]"
            aria-label="Close"
            data-testid="marketplace-pill-close"
          >
            <X size={16} strokeWidth={1.8} />
          </button>
        </div>
        {/* Sector stats strip */}
        {list.length > 0 && (
          <div className="grid grid-cols-2 gap-2 px-5 pb-3" data-testid="marketplace-pill-stats">
            <Stat label="Avg pay"      value={`$${avgPay.toFixed(2)}`} testId="marketplace-pill-stat-avg-pay" />
            <Stat label="Avg duration" value={avgDur}                  testId="marketplace-pill-stat-avg-duration" />
          </div>
        )}
        {/* Drawer body — scrollable block list */}
        <div className="px-3 pb-4 max-h-[58vh] overflow-y-auto" data-testid="marketplace-pill-list">
          {loading && (
            <div className="flex items-center justify-center py-10 text-[12px] text-[#515E7B]">
              <Loader2 size={14} className="animate-spin mr-2" /> Loading blocks…
            </div>
          )}
          {!loading && error && (
            <div className="px-3 py-6 text-[12px] text-red-600 text-center">{error}</div>
          )}
          {!loading && !error && list.length === 0 && (
            <div className="px-4 py-10 text-center text-[12px] text-[#515E7B]" data-testid="marketplace-pill-empty">
              No blocks available right now. Check back soon — new blocks are released as rider demand comes in.
            </div>
          )}
          {!loading && !error && list.map((b) => (
            <BlockRow key={b.id} block={b} onTap={() => onBlockTap?.(b)} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes pill-in {
          from { transform: translateY(40px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-pill-in { animation: pill-in 320ms cubic-bezier(0.22, 1, 0.36, 1); }
        @keyframes morph-in {
          from { transform: translateY(-8px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-morph-in { animation: morph-in 260ms cubic-bezier(0.22, 1, 0.36, 1); }
      `}</style>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-xl bg-[#F9F7F4] border border-black/[0.05] px-3 py-2" data-testid={testId}>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#515E7B]">{label}</div>
      <div className="font-serif text-[16px] mt-0.5 text-[#0A1128]">{value}</div>
    </div>
  );
}

function BlockRow({ block, onTap }) {
  // Show the actual 15-minute rider-booked pickup window
  // (e.g. "4:15 AM – 4:30 AM"). Fall back to the 4-hour bucket only
  // for legacy blocks created before pickup_time was persisted.
  const fifteenAfter = (hhmm) => {
    if (!hhmm || !hhmm.includes(":")) return null;
    const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
    const total = (h * 60 + m + 15) % (24 * 60);
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  };
  const window = block.pickup_time
    ? `${formatTime12(block.pickup_time)} – ${formatTime12(fifteenAfter(block.pickup_time))}`
    : (block.window_start && block.window_end
        ? `${formatTime12(block.window_start)} – ${formatTime12(block.window_end)}`
        : "Flexible window");
  const dur = block.estimated_minutes
    ? `${Math.floor(block.estimated_minutes / 60)}h ${block.estimated_minutes % 60}m`
    : null;
  const pref = (block.transport_preference || "shared") === "private" ? "Private" : "Shared";
  return (
    <button
      type="button"
      onClick={onTap}
      className="w-full text-left rounded-2xl bg-[#F9F7F4] hover:bg-white hover:border-[#ed6500]/40 border border-black/[0.06] mb-2 p-4 transition-all"
      data-testid={`marketplace-block-${block.id}`}
    >
      {/* Top row: pickup window + status pill */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500]">
          {window}
        </div>
        <span className="text-[9px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`marketplace-block-${block.id}-status`}>
          Available
        </span>
      </div>
      {/* Pay + claim CTA in a balanced layout */}
      <div className="flex items-center justify-between gap-3 mt-2">
        <div className="min-w-0 flex-1">
          <div className="font-serif text-[26px] leading-none text-[#0A1128]" data-testid={`marketplace-block-${block.id}-pay`}>
            ${block.pay_dollars.toFixed(2)}
          </div>
          <div className="text-[11.5px] text-[#515E7B] mt-1.5">
            {block.stops_count} stop{block.stops_count === 1 ? "" : "s"}
            {dur && ` · ${dur}`}
            {block.area_label && ` · ${block.area_label}`}
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 text-[9px] tracking-[0.22em] uppercase font-semibold text-[#515E7B]">
            <span className="px-2 py-0.5 rounded-full bg-white border border-black/[0.06]">{pref}</span>
          </div>
        </div>
        <span
          className="shrink-0 px-4 h-11 rounded-full bg-[#ed6500] text-white text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-1.5"
          data-testid={`marketplace-block-${block.id}-claim`}
        >
          View
          <ChevronRight size={12} strokeWidth={2} />
        </span>
      </div>
    </button>
  );
}

/* ====================================================================
   Block Detail Screen — opens when a block row is tapped (spec §1)
   ==================================================================== */
function BlockDetailScreen({ block, claiming, onBack, onClaim }) {
  const loading = !!block._loading;
  // 15-minute pickup window centered on the rider-booked time.
  // (e.g. pickup_time "04:15" → "4:15 AM – 4:30 AM")
  const fifteenAfter = (hhmm) => {
    if (!hhmm || !hhmm.includes(":")) return null;
    const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
    const total = (h * 60 + m + 15) % (24 * 60);
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  };
  const pickupWindow = block.pickup_time
    ? `${formatTime12(block.pickup_time)} – ${formatTime12(fifteenAfter(block.pickup_time))}`
    : (block.window_start && block.window_end
        ? `${formatTime12(block.window_start)} – ${formatTime12(block.window_end)}`
        : "Flexible");
  const dropoffDisplay = block.dropoff_time
    ? formatTime12(block.dropoff_time)
    : "—";
  const estimated = block.estimated_minutes
    ? `${Math.floor(block.estimated_minutes / 60)}h ${block.estimated_minutes % 60}m`
    : "—";
  const pref = block.transport_preference === "private" ? "Private Ride" : "Shared Ride";
  return (
    <div className="absolute inset-0 z-40 bg-[#F9F7F4] text-[#0A1128] flex flex-col" data-testid="block-detail">
      <div className="px-4 pt-[max(env(safe-area-inset-top),16px)] pb-3 border-b border-black/[0.06] bg-white flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="w-10 h-10 rounded-full bg-[#F9F7F4] hover:bg-black/[0.06] inline-flex items-center justify-center transition-colors"
          aria-label="Back"
          data-testid="block-detail-back"
        >
          <X size={16} strokeWidth={1.8} />
        </button>
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#ed6500]">Block detail</div>
          <div className="font-serif text-[18px] leading-tight" data-testid="block-detail-area">{block.area_label || "Block"}</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-5" data-testid="block-detail-summary">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#515E7B] mb-1">Pay</div>
              <div className="font-serif text-[40px] leading-none text-[#0A1128]" data-testid="block-detail-pay">${block.pay_dollars.toFixed(2)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#515E7B] mb-1">Estimated</div>
              <div className="font-serif text-[20px] leading-none text-[#0A1128]" data-testid="block-detail-estimated">{estimated}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <DetailField label="Stops"     value={block.stops_count}   testId="block-detail-stops" />
            <DetailField label="Pickups"   value={block.pickup_count}  testId="block-detail-pickups" />
            <DetailField label="Dropoffs"  value={block.dropoff_count} testId="block-detail-dropoffs" />
            <DetailField label="Area"      value={block.area_label}    testId="block-detail-area-row" />
            <DetailField label="Pickup window" value={pickupWindow} testId="block-detail-pickup-window" />
            <DetailField label="Dropoff (est.)" value={dropoffDisplay} testId="block-detail-dropoff" />
            <DetailField label="Capacity"     value={`${block.capacity_required || 1} seats`}                  testId="block-detail-capacity" />
            <DetailField label="Type"         value={pref}                                                     testId="block-detail-pref" />
          </div>
        </div>
        {loading && (
          <div className="text-center text-[12px] text-[#515E7B]"><Loader2 size={14} className="inline animate-spin mr-2" />Loading stop list…</div>
        )}
        {!loading && (block.stops || []).length > 0 && (
          <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-5" data-testid="block-detail-stops-preview">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#515E7B] mb-2">Route preview · {(block.stops || []).length} stops</div>
            <ol className="space-y-1.5">
              {(block.stops || []).slice(0, 6).map((s) => (
                <li key={s.id} className="flex items-center gap-2 text-[12px]">
                  <span className={`w-1.5 h-1.5 rounded-full ${s.kind === "pickup" ? "bg-[#ed6500]" : "bg-[#0A1128]/40"}`} />
                  <span className="font-mono text-[#515E7B]">{String(s.sequence).padStart(2, "0")}</span>
                  <span className="text-[#0A1128] truncate">{s.kind === "pickup" ? "Pickup" : "Dropoff"} · {s.rider_name}</span>
                </li>
              ))}
              {(block.stops || []).length > 6 && (
                <li className="text-[11px] text-[#515E7B] pl-3">+ {(block.stops || []).length - 6} more stops</li>
              )}
            </ol>
          </div>
        )}
      </div>

      <div className="px-4 pb-[max(env(safe-area-inset-bottom),16px)] pt-2 bg-white border-t border-black/[0.06]">
        <button
          type="button"
          onClick={onClaim}
          disabled={claiming || loading}
          className="w-full h-14 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] disabled:opacity-60 text-white text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-2 transition-colors"
          data-testid="block-detail-claim"
        >
          {claiming ? <Loader2 size={14} className="animate-spin" /> : null}
          {claiming ? "Claiming…" : "Claim block"}
        </button>
        <div className="text-[11px] text-[#515E7B] mt-2 text-center">
          {window} · First come, first served — claim is atomic.
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value, testId }) {
  return (
    <div className="rounded-xl bg-[#F9F7F4] border border-black/[0.05] px-3 py-2" data-testid={testId}>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#515E7B]">{label}</div>
      <div className="text-[13px] font-medium mt-0.5 text-[#0A1128]">{value || "—"}</div>
    </div>
  );
}

/* ====================================================================
   Utilities
   ==================================================================== */
function formatTime12(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const hour = ((h + 11) % 12) + 1;
  const am = h < 12;
  return `${hour}:${String(m).padStart(2, "0")} ${am ? "AM" : "PM"}`;
}

/** Premium DoorDash-style count marker. V3 — softer shadows, lighter
 *  label weight, tighter proportions. */
function renderCountIcon({ count, active, tier, label }) {
  // Upgraded sector marker (V4). Was a flat white circle with a slim
  // navy pill label; now a layered, glassy bubble that pops off the
  // map and clearly reads as tappable. Active state gets a warm-orange
  // ring + raised fill so a selected sector reads at a glance.
  const tierAccent = tier === "high" ? "#ed6500"
    : tier === "med"  ? "#1664E0"
    : "#515E7B";
  const size = active ? 56 : 50;
  const labelH = 17;
  const totalH = size + labelH + 6;
  const labelEsc = (label || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const labelW = Math.min(Math.max(labelEsc.length * 5.8 + 16, 56), 116);
  const totalW = Math.max(size + 18, labelW + 6);
  const cx = totalW / 2;
  const cy = size / 2 + 2;
  const ringStroke = active ? "#ed6500" : "#FFFFFF";
  const ringWidth = active ? 2.8 : 2;
  const ringR = size / 2 - 2;
  const haloR = size / 2 + 4;
  const haloFill = active ? "rgba(237,101,0,0.18)" : "rgba(22,100,224,0.12)";
  const innerFill = "#FFFFFF";
  const innerStroke = "rgba(10,17,40,0.08)";
  const countColor = active ? "#0A1128" : "#0A1128";
  const accentDotFill = tierAccent;
  const pillFill = active ? "#ed6500" : "#0A1128";
  const pillText = "#FFFFFF";
  const labelX = cx - labelW / 2;
  const labelY = size + 6;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}" viewBox="0 0 ${totalW} ${totalH}">
      <defs>
        <filter id="ds4-${count}-${active ? "a" : "i"}" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="4" stdDeviation="5" flood-color="#0A1128" flood-opacity="0.20"/>
        </filter>
        <radialGradient id="rg-${count}-${active ? "a" : "i"}" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stop-color="#FFFFFF"/>
          <stop offset="100%" stop-color="#F1EEE7"/>
        </radialGradient>
      </defs>
      <circle cx="${cx}" cy="${cy}" r="${haloR}" fill="${haloFill}" />
      <circle cx="${cx}" cy="${cy}" r="${ringR}" fill="url(#rg-${count}-${active ? "a" : "i"})" stroke="${innerStroke}" stroke-width="1" filter="url(#ds4-${count}-${active ? "a" : "i"})"/>
      <circle cx="${cx}" cy="${cy}" r="${ringR}" fill="none" stroke="${ringStroke}" stroke-width="${ringWidth}" opacity="${active ? 1 : 0}"/>
      <text x="${cx}" y="${cy + 1}" text-anchor="middle" dominant-baseline="middle"
        font-family="Inter, system-ui, -apple-system, sans-serif"
        font-weight="700" font-size="${size * 0.42}" fill="${countColor}">${count}</text>
      <circle cx="${cx + size * 0.30}" cy="${cy - size * 0.30}" r="4" fill="${accentDotFill}" stroke="${innerFill}" stroke-width="1.5"/>
      <rect x="${labelX}" y="${labelY}" width="${labelW}" height="${labelH}" rx="${labelH / 2}" fill="${pillFill}" />
      <text x="${cx}" y="${labelY + labelH / 2 + 1}" text-anchor="middle" dominant-baseline="middle"
        font-family="Inter, system-ui, -apple-system, sans-serif"
        font-weight="600" font-size="9" letter-spacing="0.6" fill="${pillText}">${labelEsc.toUpperCase()}</text>
    </svg>`;
  return {
    url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(totalW, totalH),
    anchor: new window.google.maps.Point(totalW / 2, totalH / 2),
  };
}

/** Map style — copied verbatim from `shared/components/RouteMap.jsx`
 *  MAP_STYLES_LIGHT so the driver inherits the rider's exact visual
 *  language (Swiss-editorial greys, sage parks, cream water). Do NOT
 *  diverge — rider parity is mandatory. */
const LIGHT_MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#eef1f6" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#525f7f" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#dde9d4" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry", stylers: [{ color: "#d4e2c8" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#c8dcb6" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#5a7a47" }] },
  { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#f1f3f7" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#e4e7ec" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#d0d5dd" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#bcd9f5" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "poi.medical", stylers: [{ visibility: "off" }] },
  { featureType: "poi.school", stylers: [{ visibility: "off" }] },
  { featureType: "poi.place_of_worship", stylers: [{ visibility: "off" }] },
  { featureType: "poi.sports_complex", stylers: [{ visibility: "off" }] },
  { featureType: "poi.attraction", stylers: [{ visibility: "off" }] },
  { featureType: "poi.government", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

/** Dark-mode roadmap (kept for when the driver opts into dark theme). */
const DARK_MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#0E1A33" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0E1A33" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9CA3AF" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#cbd5e1" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1B2747" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8B94A8" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2A3a66" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#08122A" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4D5B7D" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];
